import { FlashList, type FlashListRef, useRecyclingState } from '@shopify/flash-list'
import { consumeContextMenuPressSuppression } from '@tinycld/core/components/context-menu-press-guard'
import { DataTableHeader } from '@tinycld/core/components/DataTableHeader'
import { DocumentTitle } from '@tinycld/core/components/DocumentTitle'
import { EmptyState } from '@tinycld/core/components/EmptyState'
import { rowFocusStyle } from '@tinycld/core/components/focusable-row'
import { HoverAction } from '@tinycld/core/components/HoverAction'
import { LoadingState } from '@tinycld/core/components/LoadingState'
import { StarIcon } from '@tinycld/core/components/StarIcon'
import { ConfirmTrash } from '@tinycld/core/components/SuretyGuard'
import { SwipeableRow, SwipeableRowProvider } from '@tinycld/core/components/SwipeableRow'
import { useBreakpoint } from '@tinycld/core/components/workspace/useBreakpoint'
import { useAuthedThumbnailURL } from '@tinycld/core/file-viewer/use-authed-file-url'
import { formatBytes, formatDate } from '@tinycld/core/lib/format-utils'
import { refreshAllData } from '@tinycld/core/lib/pocketbase'
import { useThemeColor } from '@tinycld/core/lib/use-app-theme'
import { Image } from 'expo-image'
import { Download, FolderInput, Info, Share2, Star, Trash2 } from 'lucide-react-native'
import {
    memo,
    type ReactNode,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    type GestureResponderEvent,
    LayoutAnimation,
    type LayoutChangeEvent,
    Platform,
    Pressable,
    RefreshControl,
    Text,
    type TextStyle,
    View,
} from 'react-native'
import {
    DragGrip,
    DraggableDriveItem,
    type RenderDragPreview,
    StaticDragGrip,
    useRowDragGeometry,
} from '../components/DraggableDriveItem'
import { DriveContextMenu } from '../components/DriveContextMenu'
import { DriveDragScrollView } from '../components/DriveDragScrollView'
import { DriveItemMenuButton, DriveItemMenuIcon } from '../components/DriveItemMenuButton'
import { FolderDropTarget } from '../components/FolderDropTarget'
import { getFileIcon } from '../components/file-icons'
import { MarqueeContainer } from '../components/MarqueeContainer'
import { MarqueeOverlay } from '../components/MarqueeOverlay'
import { Thumbnail } from '../components/Thumbnail'
import { UploadingGridCard } from '../components/UploadingGridCard'
import { UploadingListRow } from '../components/UploadingListRow'
import { useDetailPanelPresentation } from '../hooks/useDetailPanelPresentation'
import { useDoubleClick } from '../hooks/useDoubleClick'
import { type DriveActions, useDrive, useRegisterDriveLayoutAnimation } from '../hooks/useDrive'
import { type GridRow, type ListRow, useGridRows, useListRows } from '../hooks/useDriveRows'
import { useDriveShortcuts } from '../hooks/useDriveShortcuts'
import { useFileSelection } from '../hooks/useFileSelection'
import { useMarqueeSelection } from '../hooks/useMarqueeSelection'
import { useOpenDriveItem } from '../hooks/useOpenDriveItem'
import { useUploadPlaceholders } from '../hooks/useUploadPlaceholders'
import { driveItemToSource } from '../lib/file-url'
import { useDriveUIStore } from '../stores/drive-ui-store'
import type { DriveItemView, SortDirection, SortField } from '../types'

interface DriveColumn {
    label: string
    flex?: number
    width?: number
    sortField?: SortField
    /** Set on a column a narrow list drops (see listColumnsFor). */
    optional?: keyof ListColumns
}

// A date ("Oct 10, 2026") and a size ("331.8 KB") never need more than these,
// so those columns keep a fixed width and the name and owner take the rest.
// A flex share instead grew them on a wide list and wrapped them onto three
// lines on a narrow one (a desktop window with the details panel open).
const DATE_COLUMN_WIDTH = 104
const SIZE_COLUMN_WIDTH = 76
const ACTIONS_COLUMN_WIDTH = 108

const DRIVE_COLUMNS: DriveColumn[] = [
    { label: 'Name', flex: 3, sortField: 'name' },
    { label: 'Owner', flex: 1, sortField: 'owner', optional: 'owner' },
    {
        label: 'Date modified',
        width: DATE_COLUMN_WIDTH,
        sortField: 'updated',
        optional: 'date',
    },
    { label: 'File size', width: SIZE_COLUMN_WIDTH, sortField: 'size', optional: 'size' },
    { label: '', width: ACTIONS_COLUMN_WIDTH },
]

const TRASH_COLUMNS: DriveColumn[] = [
    { label: 'Name', flex: 3, sortField: 'name' },
    {
        label: 'Date deleted',
        width: DATE_COLUMN_WIDTH,
        sortField: 'trashedAt',
        optional: 'date',
    },
    { label: 'File size', width: SIZE_COLUMN_WIDTH, sortField: 'size', optional: 'size' },
]

/** The optional list columns a list of the measured width has room for. */
interface ListColumns {
    owner: boolean
    date: boolean
    size: boolean
}

const ALL_LIST_COLUMNS: ListColumns = { owner: true, date: true, size: true }

// The list's side padding plus a row's own, around the cells.
const LIST_ROW_CHROME = 16 * 2 + 12 * 2
// Grip, thumbnail and enough of the name to read it.
const NAME_MIN_WIDTH = 200
const OWNER_MIN_WIDTH = 96

// A narrow list (a desktop window with the details panel open) cannot fit
// every column, and squeezing them all leaves no room for the name. The name
// and the row's buttons always show; the room left goes to the date, then the
// size, then the owner, in that order.
function listColumnsFor(listWidth: number): ListColumns {
    if (listWidth <= 0) return ALL_LIST_COLUMNS
    let room = listWidth - LIST_ROW_CHROME - NAME_MIN_WIDTH - ACTIONS_COLUMN_WIDTH
    const date = room >= DATE_COLUMN_WIDTH
    if (date) room -= DATE_COLUMN_WIDTH
    const size = date && room >= SIZE_COLUMN_WIDTH
    if (size) room -= SIZE_COLUMN_WIDTH
    const owner = size && room >= OWNER_MIN_WIDTH
    return { owner, date, size }
}

function visibleColumns(columns: DriveColumn[], shown: ListColumns): DriveColumn[] {
    return columns.filter(column => !column.optional || shown[column.optional])
}

const OWNER_CELL_STYLE = { fontSize: 12, flex: 1 }
const DATE_CELL_STYLE = { fontSize: 12, width: DATE_COLUMN_WIDTH }
const SIZE_CELL_STYLE = { fontSize: 12, width: SIZE_COLUMN_WIDTH }

function ListCell({
    isVisible,
    style,
    children,
}: {
    isVisible: boolean
    style: TextStyle
    children: ReactNode
}) {
    if (!isVisible) return null
    return (
        <Text numberOfLines={1} className="text-muted-foreground" style={style}>
            {children}
        </Text>
    )
}

const GRID_GAP = 12
const GRID_PADDING = 16
const CARD_MIN_DESKTOP = 200
const CARD_MIN_MOBILE = 150
// Grid cards (file + folder + uploading) all share this body height so
// rows in the grid layout don't have to stretch one cell to match a
// taller neighbour. 120 thumbnail area + ~36 chrome + 12 padding gap.
const GRID_CARD_HEIGHT = 168

interface ListingLayoutInfo {
    cols: number
    listColumns: ListColumns
    onLayout: (e: LayoutChangeEvent) => void
}

// The listing's measured width sets both the grid's column count and which
// optional list columns fit.
function useListingLayout(isMobile: boolean): ListingLayoutInfo {
    const cardMin = isMobile ? CARD_MIN_MOBILE : CARD_MIN_DESKTOP
    const [width, setWidth] = useState(0)
    const onLayout = useCallback((e: LayoutChangeEvent) => {
        setWidth(prev => {
            const next = e.nativeEvent.layout.width
            return prev === next ? prev : next
        })
    }, [])
    const cols = useMemo(() => {
        if (width <= 0) return 2
        const inner = width - GRID_PADDING * 2
        return Math.max(2, Math.floor((inner + GRID_GAP) / (cardMin + GRID_GAP)))
    }, [width, cardMin])
    const listColumns = useMemo(() => listColumnsFor(width), [width])
    return { cols, listColumns, onLayout }
}

interface CellContext {
    isMobile: boolean
    isTrash: boolean
    mutedColor: string
    borderColor: string
    activeIndicator: string
    dangerColor: string
    infoColor: string
    warningColor: string
    primaryColor: string
    focusedId: string | null
    isSelected: (id: string) => boolean
    handleSelect: (id: string, event: GestureResponderEvent) => void
    handleSelectClick: (id: string, event: GestureResponderEvent) => void
    actions: DriveActions
    openFile: (item: DriveItemView) => void
    itemsById: Map<string, DriveItemView>
    /** Unique per screen instance, for the Drax ids of its rows (see dragViewId). */
    dndScope: string
    /** A plain single click opens the detail panel — only where the panel sits
     *  beside the listing, since an overlay would cover what was clicked. */
    showDetailsOnClick: boolean
    /** The optional list columns there is room for. */
    listColumns: ListColumns
}

// Composes the drag-and-drop wrappers around a row/card. Every item is
// draggable (outside trash, where re-filing is disabled); folders are also
// drop targets that reparent the dropped selection. Mobile keeps long-press
// for the context menu and swipe actions, so drag is web/desktop-leaning but
// the same wrappers work on touch via Drax's long-press activation.
function DriveDnDCell({
    item,
    index,
    ctx,
    dragBy,
    children,
}: {
    item: DriveItemView
    /** The row's position in the list; see the drop target's id below. */
    index: number
    ctx: CellContext
    /** 'grip' in list view (the row renders its own grip), 'card' in grid view. */
    dragBy: 'grip' | 'card'
    children: ReactNode
}) {
    const renderCardPreview: RenderDragPreview = size => ({
        content: (
            <GridCardDragPreview item={item} mutedColor={ctx.mutedColor} width={size?.width} />
        ),
    })
    // Grid view drags the whole card (small enough that Drax's hit-test tracks
    // the finger). List rows are full-width, so wrapping the row as the
    // draggable would push the hit-test point far off the finger (it's anchored
    // at finger − grabOffset + sourceWidth/2) and miss narrow targets like the
    // sidebar — so list rows instead drag via a finger-sized <DragGrip> the row
    // renders itself, and the cell adds no draggable wrapper here.
    const content =
        dragBy === 'card' ? (
            <DraggableDriveItem
                itemId={item.id}
                dndScope={ctx.dndScope}
                renderPreview={renderCardPreview}
                isEnabled={!ctx.isTrash}
            >
                {children}
            </DraggableDriveItem>
        ) : (
            children
        )
    if (ctx.isTrash || !item.isFolder) return content
    return (
        <FolderDropTarget
            // Drax measures a view only when it registers or resizes. An item
            // inserted or removed above this folder moves it without resizing
            // it (a realtime update can land mid-drag), so the id carries the
            // position too: a move re-registers the target and measures it again.
            id={`drive-drop-${ctx.dndScope}-${item.id}-${index}`}
            targetFolderId={item.id}
            itemsById={ctx.itemsById}
            onDropItems={ctx.actions.moveItems}
        >
            {content}
        </FolderDropTarget>
    )
}

export default function DriveScreen() {
    const drive = useDrive()
    const {
        viewMode,
        activeSection,
        currentFolderId,
        currentItems,
        searchQuery,
        isSearching,
        isLoading,
        openPrompt,
        actions,
        itemsById,
        userId,
        sortField,
        sortDirection,
        setSort,
    } = drive
    const isSearchActive = searchQuery.length >= 2
    const isTrash = activeSection === 'trash'
    const isMobile = useBreakpoint() === 'mobile'

    // Subscribe to upload progress here (not via DriveContextValue) so the
    // toolbar/dialogs/sidebar/preview don't re-render on every progress tick.
    const uploadPlaceholders = useUploadPlaceholders({
        userId,
        activeSection,
        currentFolderId,
        isSearchActive,
        itemsById,
    })

    // Theme colors are global — read once at the screen level so all rows
    // share the same JS-side cache. useThemeColor hits getComputedStyle on
    // the document element; doing that 3-4× per row × 50 rows would re-pay
    // the cost on every toggle.
    const mutedColor = useThemeColor('muted-foreground')
    const borderColor = useThemeColor('border')
    const activeIndicator = useThemeColor('active-indicator')
    const dangerColor = useThemeColor('danger')
    const infoColor = useThemeColor('info')
    const warningColor = useThemeColor('warning')
    const primaryColor = useThemeColor('primary')

    const [isRefreshing, setIsRefreshing] = useState(false)
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true)
        try {
            await refreshAllData()
        } finally {
            setIsRefreshing(false)
        }
    }, [])

    // Upload placeholders are appended to the file list; merging happens here
    // (rather than in useDriveItems) so progress ticks only re-render the
    // screen, not every useDrive() consumer.
    const { folders, files } = useMemo(
        () => ({
            folders: currentItems.filter(i => i.isFolder),
            files: [...currentItems.filter(i => !i.isFolder), ...uploadPlaceholders],
        }),
        [currentItems, uploadPlaceholders]
    )

    const { cols, listColumns, onLayout } = useListingLayout(isMobile)
    const listHeaderColumns = useMemo(
        () => visibleColumns(isTrash ? TRASH_COLUMNS : DRIVE_COLUMNS, listColumns),
        [isTrash, listColumns]
    )
    const listRows = useListRows({ folders, files, sortField, sortDirection })
    const gridRows = useGridRows({ folders, files })

    // Navigable items power keyboard nav and shift-range selection. Skips
    // upload placeholders since they aren't actionable.
    const navigableItems = useMemo(
        () => [...folders, ...files.filter(i => !i.uploadStatus)],
        [folders, files]
    )
    const orderedIds = useMemo(() => navigableItems.map(i => i.id), [navigableItems])
    const { handleSelect, handleSelectClick, isSelected } = useFileSelection(orderedIds)
    const openFile = useOpenDriveItem(actions)

    const selectToggle = useDriveUIStore(s => s.selectToggle)
    const { focusedId } = useDriveShortcuts({
        items: navigableItems,
        toggleSelect: selectToggle,
        openItem: openFile,
        onNewFolder: () => openPrompt({ type: 'new-folder' }),
        isEnabled: !isTrash,
        listKey: `${activeSection}:${currentFolderId}`,
    })

    const listFlashRef = useRef<FlashListRef<ListRow>>(null)
    const gridFlashRef = useRef<FlashListRef<GridRow>>(null)

    // Drag-to-select (web/desktop only). The gesture lives on the file-area
    // container and reads the selection imperatively at drag-start, so the hook
    // doesn't re-subscribe on every selection change.
    const marqueeContainerRef = useRef<HTMLDivElement>(null)
    const setSelection = useDriveUIStore(s => s.setSelection)
    const clearSelection = useDriveUIStore(s => s.clearSelection)
    const getBaseSelection = useCallback(() => useDriveUIStore.getState().selectedIds, [])
    // The active list's ref, type-erased for the marquee's edge auto-scroll.
    const activeScrollRef = (
        viewMode === 'list' ? listFlashRef : gridFlashRef
    ) as React.RefObject<FlashListRef<unknown> | null>
    const { marqueeRect, isDragging } = useMarqueeSelection({
        containerRef: marqueeContainerRef,
        setSelection,
        clearSelection,
        getBaseSelection,
        scrollRef: activeScrollRef,
        enabled: !isMobile,
    })

    // Read rows through refs so the scroll-to-focused effect doesn't re-fire
    // when row arrays churn identity unrelated to focus. Only focusedId
    // changes should drive a programmatic scroll.
    const listRowsRef = useRef(listRows)
    const gridRowsRef = useRef(gridRows)
    listRowsRef.current = listRows
    gridRowsRef.current = gridRows

    useEffect(() => {
        if (!focusedId) return
        if (viewMode === 'list') {
            const idx = listRowsRef.current.findIndex(r => r.item.id === focusedId)
            if (idx < 0) return
            listFlashRef.current?.scrollToIndex({ index: idx, viewPosition: 0.5, animated: true })
        } else {
            const idx = gridRowsRef.current.findIndex(
                r => r.kind === 'card' && r.item.id === focusedId
            )
            if (idx < 0) return
            gridFlashRef.current?.scrollToIndex({ index: idx, viewPosition: 0.5, animated: true })
        }
    }, [focusedId, viewMode])

    // LayoutAnimation is experimental on Android per FlashList's docs, so
    // we skip the prep there. The active list (whichever ref is mounted)
    // gets prepareForLayoutAnimationRender called before each reorder
    // mutation; only one ref will resolve to a mounted FlashList at a
    // time, so it's safe to fire both.
    const prepareLayoutAnimation = useCallback(() => {
        if (Platform.OS === 'android') return
        listFlashRef.current?.prepareForLayoutAnimationRender()
        gridFlashRef.current?.prepareForLayoutAnimationRender()
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
    }, [])
    useRegisterDriveLayoutAnimation(prepareLayoutAnimation)

    const dndScope = useId()
    const showDetailsOnClick = useDetailPanelPresentation() === 'inline'
    const cellContext: CellContext = useMemo(
        () => ({
            isMobile,
            isTrash,
            mutedColor,
            borderColor,
            activeIndicator,
            dangerColor,
            infoColor,
            warningColor,
            primaryColor,
            focusedId,
            isSelected,
            handleSelect,
            handleSelectClick,
            actions,
            openFile,
            itemsById,
            dndScope,
            showDetailsOnClick,
            listColumns,
        }),
        [
            isMobile,
            isTrash,
            mutedColor,
            borderColor,
            activeIndicator,
            dangerColor,
            infoColor,
            warningColor,
            primaryColor,
            focusedId,
            isSelected,
            handleSelect,
            handleSelectClick,
            actions,
            openFile,
            itemsById,
            dndScope,
            showDetailsOnClick,
            listColumns,
        ]
    )

    if (isSearching) {
        return <LoadingState message="Searching…" />
    }

    const hasAnything = currentItems.length > 0 || uploadPlaceholders.length > 0
    if (isLoading && !hasAnything) {
        return <LoadingState />
    }

    if (!hasAnything) {
        let message = 'No files in this location'
        if (isSearchActive) message = `No results for "${searchQuery}"`
        else if (isTrash) message = 'Trash is empty'
        return <EmptyState message={message} />
    }

    return (
        <SwipeableRowProvider>
            <DocumentTitle pkg="Drive" />
            <MarqueeContainer containerRef={marqueeContainerRef} onLayout={onLayout}>
                {viewMode === 'list' ? (
                    <DriveListMode
                        rows={listRows}
                        flashRef={listFlashRef}
                        cellContext={cellContext}
                        columns={listHeaderColumns}
                        showColumnHeader={!isMobile}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={setSort}
                        isMobile={isMobile}
                        isRefreshing={isRefreshing}
                        onRefresh={handleRefresh}
                    />
                ) : (
                    <DriveGridMode
                        rows={gridRows}
                        cols={cols}
                        flashRef={gridFlashRef}
                        cellContext={cellContext}
                        isMobile={isMobile}
                        isRefreshing={isRefreshing}
                        onRefresh={handleRefresh}
                    />
                )}
                <MarqueeOverlay rect={marqueeRect} visible={isDragging} />
            </MarqueeContainer>
        </SwipeableRowProvider>
    )
}

interface ListModeProps {
    rows: ListRow[]
    flashRef: React.RefObject<FlashListRef<ListRow> | null>
    cellContext: CellContext
    columns: DriveColumn[]
    showColumnHeader: boolean
    sortField: SortField
    sortDirection: SortDirection
    onSort: (field: SortField) => void
    isMobile: boolean
    isRefreshing: boolean
    onRefresh: () => void
}

function DriveListMode({
    rows,
    flashRef,
    cellContext,
    columns,
    showColumnHeader,
    sortField,
    sortDirection,
    onSort,
    isMobile,
    isRefreshing,
    onRefresh,
}: ListModeProps) {
    const renderItem = useCallback(
        ({ item, extraData }: { item: ListRow; extraData?: CellContext }) => {
            const ctx = extraData ?? cellContext
            const it = item.item
            if (it.uploadStatus) {
                return <UploadingListRow item={it} onDismiss={ctx.actions.dismissUpload} />
            }
            if (ctx.isTrash) {
                return (
                    <DriveContextMenu item={it}>
                        <TrashListRow item={it} ctx={ctx} />
                    </DriveContextMenu>
                )
            }
            return (
                <DriveContextMenu item={it}>
                    <DriveDnDCell item={it} index={item.index} ctx={ctx} dragBy="grip">
                        <FilesListRow item={it} index={item.index} ctx={ctx} />
                    </DriveDnDCell>
                </DriveContextMenu>
            )
        },
        [cellContext]
    )

    const keyExtractor = useCallback((row: ListRow) => row.item.id, [])
    const getItemType = useCallback(
        (row: ListRow) => (row.item.uploadStatus ? 'upload' : 'file'),
        []
    )

    const ListHeader = useMemo(
        () =>
            showColumnHeader ? (
                <DataTableHeader
                    columns={columns}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={onSort}
                />
            ) : null,
        [showColumnHeader, columns, sortField, sortDirection, onSort]
    )

    return (
        <FlashList<ListRow>
            // Remount on sort change. A pure reorder of `data` (same item ids,
            // same item type) doesn't reliably reflow FlashList's recycled row
            // positions — the cells keep their old visual slots. Keying on the
            // sort signal forces a fresh layout when the order changes; sorting
            // is a deliberate, infrequent action where resetting scroll to the
            // top is the expected behaviour anyway.
            key={`${sortField}:${sortDirection}`}
            ref={flashRef}
            data={rows}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemType={getItemType}
            extraData={cellContext}
            renderScrollComponent={DriveDragScrollView}
            contentContainerStyle={{ paddingHorizontal: isMobile ? 0 : 16 }}
            ListHeaderComponent={ListHeader}
            refreshControl={
                isMobile ? (
                    <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
                ) : undefined
            }
        />
    )
}

interface GridModeProps {
    rows: GridRow[]
    cols: number
    flashRef: React.RefObject<FlashListRef<GridRow> | null>
    cellContext: CellContext
    isMobile: boolean
    isRefreshing: boolean
    onRefresh: () => void
}

function DriveGridMode({
    rows,
    cols,
    flashRef,
    cellContext,
    isMobile,
    isRefreshing,
    onRefresh,
}: GridModeProps) {
    const renderItem = useCallback(
        ({ item, index, extraData }: { item: GridRow; index: number; extraData?: CellContext }) => {
            const ctx = extraData ?? cellContext
            if (item.kind === 'section') {
                return <GridSectionHeader title={item.title} />
            }
            const it = item.item
            return (
                <View style={{ paddingHorizontal: GRID_GAP / 2, paddingBottom: GRID_GAP }}>
                    {it.uploadStatus ? (
                        <UploadingGridCard item={it} onDismiss={ctx.actions.dismissUpload} />
                    ) : (
                        <DriveContextMenu item={it}>
                            <DriveDnDCell item={it} index={index} ctx={ctx} dragBy="card">
                                {it.isFolder ? (
                                    <FolderGridCard item={it} ctx={ctx} />
                                ) : (
                                    <FileGridCard item={it} ctx={ctx} />
                                )}
                            </DriveDnDCell>
                        </DriveContextMenu>
                    )}
                </View>
            )
        },
        [cellContext]
    )

    const keyExtractor = useCallback((row: GridRow, _index: number) => {
        if (row.kind === 'section') return `__section_${row.title}__`
        return row.item.id
    }, [])

    const getItemType = useCallback((row: GridRow) => {
        if (row.kind === 'section') return 'section'
        const it = row.item
        if (it.uploadStatus) return 'upload'
        if (it.isFolder) return 'folder'
        return 'file'
    }, [])

    // Opening or closing the inline detail panel (or resizing the window)
    // changes the column count, and every card moves. Bring the selected card
    // back into view if the reflow moved it off screen. The scroll position
    // belongs to FlashList, outside React, hence an effect on the column count.
    const rowsRef = useRef(rows)
    rowsRef.current = rows
    const laidOutColsRef = useRef(cols)
    useEffect(() => {
        if (laidOutColsRef.current === cols) return
        laidOutColsRef.current = cols
        const { selectedItemId } = useDriveUIStore.getState()
        const index = rowsRef.current.findIndex(
            r => r.kind === 'card' && r.item.id === selectedItemId
        )
        const list = flashRef.current
        const layout = index < 0 ? undefined : list?.getLayout(index)
        if (!list || !layout) return
        const top = layout.y + list.getFirstItemOffset() - list.getAbsoluteLastScrollOffset()
        if (top >= 0 && top + layout.height <= list.getWindowSize().height) return
        list.scrollToIndex({ index, viewPosition: 0.5, animated: true })
    }, [cols, flashRef])

    const overrideItemLayout = useCallback(
        (layout: { span?: number }, row: GridRow) => {
            if (row.kind === 'section') {
                layout.span = cols
            }
        },
        [cols]
    )

    return (
        <FlashList<GridRow>
            ref={flashRef}
            data={rows}
            numColumns={cols}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemType={getItemType}
            overrideItemLayout={overrideItemLayout}
            extraData={cellContext}
            renderScrollComponent={DriveDragScrollView}
            contentContainerStyle={{ paddingHorizontal: GRID_PADDING - GRID_GAP / 2 }}
            refreshControl={
                isMobile ? (
                    <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
                ) : undefined
            }
        />
    )
}

// Opens the detail panel for a plain single click, once the double-click window
// has passed: opening it narrows the listing (and reflows the grid), so doing
// it between the two clicks of a double-click would move the item out from
// under the second one. The click already selected the item, and the panel
// shows the selected item. A modified click extends the selection instead.
function useShowDetailsAfterClick(ctx: CellContext) {
    const { showDetailsOnClick, actions } = ctx
    return useCallback(
        (event: GestureResponderEvent) => {
            if (!showDetailsOnClick) return
            const native = event.nativeEvent as unknown as MouseEvent
            if (native.metaKey || native.ctrlKey || native.shiftKey) return
            actions.openDetailPanel()
        },
        [showDetailsOnClick, actions]
    )
}

const FilesListRow = memo(FilesListRowImpl)
function FilesListRowImpl({
    item,
    index,
    ctx,
}: {
    item: DriveItemView
    index: number
    ctx: CellContext
}) {
    const { actions } = ctx
    const handleInfo = useCallback(() => {
        actions.selectItem(item.id)
        actions.openDetailPanel()
    }, [item.id, actions])
    // Hover state must reset when the cell recycles to a different item,
    // otherwise the wrong row briefly renders hovered for one frame after
    // a fast scroll.
    const [isHovered, setIsHovered] = useRecyclingState(false, [item.id])
    const geometry = useRowDragGeometry()

    // Select on press-down (onPressIn) for instant highlight. Applies to
    // folders and files alike: a single click selects, a double click opens.
    // Modifier (toggle/range) selection is resolved on the click instead,
    // because the modifier flag isn't reliable on pointerdown under load
    // (handleSelect/pressDownAction no-op on a modified press).
    const handleSelectIn = useCallback(
        (event: GestureResponderEvent) => {
            const native = event.nativeEvent as unknown as MouseEvent
            const hasModifier = native.metaKey || native.ctrlKey || native.shiftKey
            if (hasModifier) return // resolved on click (reliable modifier)
            ctx.handleSelect(item.id, event)
        },
        [item.id, ctx]
    )
    // Open on double-click, select on single — the same contract for folders and
    // files, and the one every desktop file manager uses. A folder opens into
    // itself; a file opens its preview (or its registered app).
    //
    // Folders used to navigate on a plain single click, which made them
    // impossible to select without a modifier: any toolbar action scoped to a
    // selection (Rename, Move, Share) was unreachable for a folder by mouse.
    //
    // The single-click slot also resolves the deferred modifier selection
    // (toggle/range) and collapses a preserved multi-selection to this row
    // (handleSelect keeps the set on press-down so a grab can drag it). Runs
    // only on a real click since a drag consumes the pointer.
    const handleRowClickSelect = useCallback(
        (event: GestureResponderEvent) => ctx.handleSelectClick(item.id, event),
        [item.id, ctx]
    )
    // ctx.openFile IS the shared opener (useOpenDriveItem): folders navigate,
    // files hand off to a registered app (calc/text) or fall back to preview.
    const handleOpenAction = useCallback(() => ctx.openFile(item), [item, ctx])
    const handleShowDetails = useShowDetailsAfterClick(ctx)
    const handlePress = useDoubleClick(handleRowClickSelect, handleOpenAction, handleShowDetails)

    const handleMobilePress = useCallback(() => {
        // A long-press opened the context menu; ignore the tap that fires when
        // the finger lifts so the file doesn't open out from under the menu.
        if (consumeContextMenuPressSuppression(Date.now())) return
        ctx.openFile(item)
    }, [item, ctx])

    const swipeActions = useMemo(
        () => [
            {
                icon: Trash2,
                label: 'Delete',
                onPress: () => actions.moveToTrash(item.id),
                backgroundColor: ctx.dangerColor,
            },
            // Move-to-folder, the mobile counterpart to the desktop right-click
            // "Move". Trashed items can't be re-filed, so it's omitted in trash.
            ...(ctx.isTrash
                ? []
                : [
                      {
                          icon: FolderInput,
                          label: 'Move',
                          onPress: () => actions.openMoveDialog(item.id, item.name),
                          backgroundColor: ctx.primaryColor,
                      },
                  ]),
            {
                icon: Download,
                label: 'Download',
                onPress: () => actions.downloadItem(item.id),
                backgroundColor: ctx.infoColor,
            },
            {
                icon: Star,
                label: item.starred ? 'Unstar' : 'Star',
                onPress: () => actions.toggleStar(item.id),
                backgroundColor: ctx.warningColor,
            },
        ],
        [
            item.id,
            item.name,
            item.starred,
            actions,
            ctx.isTrash,
            ctx.dangerColor,
            ctx.infoColor,
            ctx.warningColor,
            ctx.primaryColor,
        ]
    )

    if (ctx.isMobile) {
        const mobileRow = (
            <Pressable
                onPress={handleMobilePress}
                testID={`drive-item-${item.name}`}
                // No accessibilityRole="button": the row contains its own
                // buttons (star, ⋯ menu), and a <button> can't nest a <button>
                // on web. A labelled clickable region is the correct semantic.
                accessibilityLabel={`${item.name} ${formatDate(item.updated)}`}
                className="flex-row items-center px-4 py-3 border-b border-border gap-3"
            >
                <ListRowThumbnail
                    item={item}
                    size={40}
                    fallbackIconSize={24}
                    mutedColor={ctx.mutedColor}
                />
                <View className="flex-1 gap-0.5">
                    <Text
                        numberOfLines={1}
                        className="text-foreground"
                        style={{ fontSize: 16, fontWeight: '500' }}
                    >
                        {item.name}
                    </Text>
                    <Text
                        numberOfLines={1}
                        className="text-muted-foreground"
                        style={{ fontSize: 12 }}
                    >
                        {formatDate(item.updated)}
                        {item.isFolder ? '' : ` · ${formatBytes(item.size)}`}
                    </Text>
                </View>
                <Pressable
                    className="p-1"
                    onPress={e => {
                        e.stopPropagation()
                        actions.toggleStar(item.id)
                    }}
                >
                    <StarIcon isStarred={item.starred} size={18} />
                </Pressable>
                <DriveItemMenuButton item={item} size={18} />
            </Pressable>
        )

        return <SwipeableRow actions={swipeActions}>{mobileRow}</SwipeableRow>
    }

    const isFocused = item.id === ctx.focusedId
    const isSelectedRow = ctx.isSelected(item.id)
    const effectStyle = rowFocusStyle({
        isFocused,
        isHovered,
        borderColor: ctx.borderColor,
        activeIndicator: ctx.activeIndicator,
    })
    const tooltipPosition = index === 0 ? ('below' as const) : ('above' as const)
    const focusTint = effectStyle?.backgroundColor
    const rowTint =
        typeof focusTint === 'string'
            ? focusTint
            : isSelectedRow
              ? `${ctx.activeIndicator}12`
              : undefined
    // The row drags by its grip, so the preview copies the whole row and is
    // anchored at the grip's spot in it.
    const renderRowPreview: RenderDragPreview = () => {
        const { width, height, anchor } = geometry.read()
        return {
            anchor,
            content: (
                <DesktopListRowDragPreview
                    item={item}
                    mutedColor={ctx.mutedColor}
                    listColumns={ctx.listColumns}
                    activeIndicator={ctx.activeIndicator}
                    width={width}
                    height={height}
                />
            ),
        }
    }

    const hoverWebProps =
        Platform.OS === 'web'
            ? {
                  onMouseEnter: () => setIsHovered(true),
                  onMouseLeave: () => setIsHovered(false),
              }
            : {}

    return (
        <Pressable
            onPress={handlePress}
            onPressIn={handleSelectIn}
            testID={`drive-item-${item.name}`}
            // No "button" role — the row hosts its own buttons (star, ⋯ menu);
            // nesting <button> in <button> is invalid. Labelled clickable row.
            accessibilityLabel={`${item.name} ${item.owner} ${formatDate(item.updated)}`}
            // Tags the row for the drag-to-select hit-test (full-width row rect).
            dataSet={{ driveItemId: item.id }}
            className={`${LIST_ROW_CLASS_NAME} ${isSelectedRow ? '' : 'bg-background'}`}
            style={[isSelectedRow ? selectedRowStyle(ctx.activeIndicator) : null, effectStyle]}
            onLayout={geometry.onRowLayout}
            {...hoverWebProps}
        >
            <DesktopListRowFace
                item={item}
                mutedColor={ctx.mutedColor}
                listColumns={ctx.listColumns}
                onLeadingLayout={geometry.onLeadingLayout}
                grip={
                    ctx.isTrash ? null : (
                        <DragGrip
                            itemId={item.id}
                            dndScope={ctx.dndScope}
                            renderPreview={renderRowPreview}
                            onLayout={geometry.onGripLayout}
                        />
                    )
                }
                trailing={
                    <>
                        {/* The hover icons overflow left over the date and size
                            cells, so they sit on an opaque background, tinted
                            like the row (selection or keyboard focus) so they
                            read as part of it. */}
                        <View
                            className={isHovered ? 'bg-background' : undefined}
                            style={
                                isHovered ? undefined : { width: 0, opacity: 0, overflow: 'hidden' }
                            }
                            pointerEvents={isHovered ? 'auto' : 'none'}
                        >
                            <View
                                className="flex-row items-center pl-1"
                                style={{ backgroundColor: rowTint }}
                            >
                                <HoverAction
                                    icon={Info}
                                    label="Info"
                                    onPress={handleInfo}
                                    tooltipPosition={tooltipPosition}
                                />
                                <ConfirmTrash
                                    itemName={item.name}
                                    onConfirmed={() => actions.moveToTrash(item.id)}
                                >
                                    {onOpen => (
                                        <HoverAction
                                            icon={Trash2}
                                            label="Delete"
                                            onPress={onOpen}
                                            tooltipPosition={tooltipPosition}
                                        />
                                    )}
                                </ConfirmTrash>
                                <HoverAction
                                    icon={Download}
                                    label="Download"
                                    onPress={() => actions.downloadItem(item.id)}
                                    tooltipPosition={tooltipPosition}
                                />
                                <HoverAction
                                    icon={Share2}
                                    label="Share"
                                    onPress={() => actions.openShareDialog(item.id, item.name)}
                                    tooltipPosition={tooltipPosition}
                                />
                            </View>
                        </View>
                        <Pressable
                            style={{ padding: 4 }}
                            onPress={e => {
                                e.stopPropagation()
                                actions.toggleStar(item.id)
                            }}
                        >
                            <StarIcon isStarred={item.starred} size={16} />
                        </Pressable>
                        <DriveItemMenuButton item={item} />
                    </>
                }
            />
        </Pressable>
    )
}

const LIST_ROW_CLASS_NAME = 'flex-row items-center px-3 py-2.5 border-b border-border'

function selectedRowStyle(activeIndicator: string) {
    return { backgroundColor: `${activeIndicator}12` }
}

// What a desktop list row shows, shared by the live row and its drag preview so
// the two cannot drift apart.
function DesktopListRowFace({
    item,
    mutedColor,
    listColumns,
    grip,
    trailing,
    onLeadingLayout,
}: {
    item: DriveItemView
    mutedColor: string
    /** The optional columns there is room for. */
    listColumns: ListColumns
    /** The drag grip, or its look without the gesture in a preview. */
    grip: ReactNode
    /** The row's buttons, or their look without the buttons in a preview. */
    trailing: ReactNode
    onLeadingLayout?: (event: LayoutChangeEvent) => void
}) {
    return (
        <>
            <View
                className="flex-row items-center"
                style={{ gap: 10, flex: 3 }}
                onLayout={onLeadingLayout}
            >
                {grip}
                <ListRowThumbnail
                    item={item}
                    size={28}
                    fallbackIconSize={20}
                    mutedColor={mutedColor}
                />
                <Text
                    numberOfLines={1}
                    className="flex-1 text-foreground"
                    style={{
                        fontSize: 13,
                        fontWeight: '500',
                    }}
                >
                    {item.name}
                </Text>
            </View>
            <ListCell isVisible={listColumns.owner} style={OWNER_CELL_STYLE}>
                {item.owner}
            </ListCell>
            <ListCell isVisible={listColumns.date} style={DATE_CELL_STYLE}>
                {formatDate(item.updated)}
            </ListCell>
            <ListCell isVisible={listColumns.size} style={SIZE_CELL_STYLE}>
                {item.isFolder ? '—' : formatBytes(item.size)}
            </ListCell>
            {/* Trailing column: the quick-action icons (Info, Delete, Download,
                Share) reveal on hover, followed by the always-visible ★ star +
                ⋯ menu. Laid out in normal flow (left → right) so nothing
                overlaps the ⋯ trigger (an absolute overlay there stole its
                click). The hover group collapses to zero width when not hovered
                so the row layout doesn't shift. */}
            <View
                className="flex-row items-center justify-end"
                style={{ width: ACTIONS_COLUMN_WIDTH, flexShrink: 0 }}
            >
                {trailing}
            </View>
        </>
    )
}

// The row's copy that follows the pointer: the same face, inert, at the size
// the source row measured — its height includes the collapsed hover buttons,
// which the copy leaves out. The live row is transparent when selected, so the
// copy lays the selection tint over an opaque background.
function DesktopListRowDragPreview({
    item,
    mutedColor,
    listColumns,
    activeIndicator,
    width,
    height,
}: {
    item: DriveItemView
    mutedColor: string
    listColumns: ListColumns
    activeIndicator: string
    width: number
    height: number
}) {
    const isSelected = useDriveUIStore(s => s.selectedIds.has(item.id))
    return (
        <View className="bg-background shadow-md" style={{ width, height }}>
            <View
                className={`${LIST_ROW_CLASS_NAME} flex-1`}
                style={isSelected ? selectedRowStyle(activeIndicator) : null}
            >
                <DesktopListRowFace
                    item={item}
                    mutedColor={mutedColor}
                    listColumns={listColumns}
                    grip={<StaticDragGrip />}
                    trailing={
                        <>
                            <View style={{ padding: 4 }}>
                                <StarIcon isStarred={item.starred} size={16} />
                            </View>
                            <DriveItemMenuIcon />
                        </>
                    }
                />
            </View>
        </View>
    )
}

const ListRowThumbnail = memo(ListRowThumbnailImpl)
function ListRowThumbnailImpl({
    item,
    size,
    fallbackIconSize,
    mutedColor,
}: {
    item: DriveItemView
    size: number
    fallbackIconSize: number
    mutedColor: string
}) {
    const { icon: FileIcon, color: iconColor } = getFileIcon(item.category, mutedColor)
    const { url: thumbnailUrl } = useAuthedThumbnailURL(
        item.isFolder ? undefined : driveItemToSource(item),
        `${size * 2}x${size * 2}`
    )

    if (!thumbnailUrl) {
        return (
            <View
                style={{
                    width: size,
                    height: size,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <FileIcon size={fallbackIconSize} color={iconColor} />
            </View>
        )
    }

    return (
        <Image
            // cacheKey pins the disk/memory cache entry to the stable item id
            // rather than the URL, so the rotating ?token= doesn't bust the
            // cache (the token changes ~every 90s but the file doesn't). On web
            // expo-image renders a plain <img> and ignores cacheKey — the
            // browser still caches by URL — so the token-rotation win is
            // native-only; stabilising the web URL is a separate core change.
            source={{ uri: thumbnailUrl, cacheKey: item.id }}
            style={{ width: size, height: size, borderRadius: 4 }}
            contentFit="cover"
            cachePolicy="memory-disk"
            recyclingKey={item.id}
            // No fade in the list — a per-cell transition reads as flicker as
            // FlashList recycles rows during fast scrolls.
            transition={0}
        />
    )
}

const TrashListRow = memo(TrashListRowImpl)
function TrashListRowImpl({ item, ctx }: { item: DriveItemView; ctx: CellContext }) {
    const isSelectedRow = ctx.isSelected(item.id)

    if (ctx.isMobile) {
        return (
            <Pressable
                onPress={e => ctx.handleSelect(item.id, e)}
                // No "button" role — the row hosts its own buttons; nesting
                // <button> in <button> is invalid on web.
                accessibilityLabel={`${item.name} ${formatDate(item.trashedAt)}`}
                className="flex-row items-center px-4 py-3 border-b border-border gap-3"
            >
                <ListRowThumbnail
                    item={item}
                    size={40}
                    fallbackIconSize={24}
                    mutedColor={ctx.mutedColor}
                />
                <View className="flex-1 gap-0.5">
                    <Text
                        numberOfLines={1}
                        className="text-foreground"
                        style={{ fontSize: 16, fontWeight: '500' }}
                    >
                        {item.name}
                    </Text>
                    <Text
                        numberOfLines={1}
                        className="text-muted-foreground"
                        style={{ fontSize: 12 }}
                    >
                        Deleted {formatDate(item.trashedAt)}
                        {item.isFolder ? '' : ` · ${formatBytes(item.size)}`}
                    </Text>
                </View>
            </Pressable>
        )
    }

    return (
        <Pressable
            onPress={e => ctx.handleSelect(item.id, e)}
            // No "button" role — the row hosts its own buttons; nesting
            // <button> in <button> is invalid on web.
            accessibilityLabel={`${item.name} ${formatDate(item.trashedAt)}`}
            // Tags the row for the drag-to-select hit-test (full-width row rect).
            dataSet={{ driveItemId: item.id }}
            className="flex-row items-center px-3 py-2.5 border-b border-border"
            style={isSelectedRow ? { backgroundColor: `${ctx.activeIndicator}12` } : undefined}
        >
            <View className="flex-row items-center" style={{ gap: 10, flex: 3 }}>
                <ListRowThumbnail
                    item={item}
                    size={28}
                    fallbackIconSize={20}
                    mutedColor={ctx.mutedColor}
                />
                <Text
                    numberOfLines={1}
                    className="flex-1 text-foreground"
                    style={{
                        fontSize: 13,
                        fontWeight: '500',
                    }}
                >
                    {item.name}
                </Text>
            </View>
            <ListCell isVisible={ctx.listColumns.date} style={DATE_CELL_STYLE}>
                {formatDate(item.trashedAt)}
            </ListCell>
            <ListCell isVisible={ctx.listColumns.size} style={SIZE_CELL_STYLE}>
                {item.isFolder ? '—' : formatBytes(item.size)}
            </ListCell>
        </Pressable>
    )
}

function GridSectionHeader({ title }: { title: string }) {
    return (
        <Text
            className="uppercase text-muted-foreground"
            style={{
                fontSize: 12,
                fontWeight: '600',
                letterSpacing: 0.5,
                marginTop: 8,
                marginBottom: 10,
                paddingHorizontal: GRID_GAP / 2,
            }}
        >
            {title}
        </Text>
    )
}

const FolderGridCard = memo(FolderGridCardImpl)
function FolderGridCardImpl({ item, ctx }: { item: DriveItemView; ctx: CellContext }) {
    const { actions } = ctx
    const isSelectedRow = ctx.isSelected(item.id)

    const handleNavigate = useCallback(() => actions.navigateToFolder(item.id), [item.id, actions])

    // Desktop: single click selects, double click opens — the same contract as
    // a file card, so a folder can be selected for Rename/Move/Share without a
    // modifier. Touch keeps tap-to-open, which is the platform convention (and
    // where a long-press, not a second tap, is how you select).
    const handleSelectIn = useCallback(
        (event: GestureResponderEvent) => {
            const native = event.nativeEvent as unknown as MouseEvent
            if (native.metaKey || native.ctrlKey || native.shiftKey) return
            ctx.handleSelect(item.id, event)
        },
        [item.id, ctx]
    )
    const handleClickSelect = useCallback(
        (event: GestureResponderEvent) => ctx.handleSelectClick(item.id, event),
        [item.id, ctx]
    )
    const handleShowDetails = useShowDetailsAfterClick(ctx)
    const handleDesktopOpen = useDoubleClick(handleClickSelect, handleNavigate, handleShowDetails)
    const handleMobilePress = useCallback(() => {
        if (consumeContextMenuPressSuppression(Date.now())) return
        handleNavigate()
    }, [handleNavigate])

    return (
        <Pressable
            onPressIn={ctx.isMobile ? undefined : handleSelectIn}
            onPress={ctx.isMobile ? handleMobilePress : handleDesktopOpen}
            testID={`drive-item-${item.name}`}
            // No "button" role — the card hosts its own buttons (⋯ menu);
            // nesting <button> in <button> is invalid on web.
            accessibilityLabel={`${item.name} ${formatDate(item.updated)}`}
            // Tags the card for the drag-to-select hit-test (its rect == the
            // visible tile). Renders as data-drive-item-id on web; no-op native.
            dataSet={{ driveItemId: item.id }}
            style={{ height: GRID_CARD_HEIGHT }}
            className={gridCardClassName(item, isSelectedRow)}
        >
            <GridCardFace
                item={item}
                mutedColor={ctx.mutedColor}
                headerActions={<GridCardActions item={item} actions={actions} />}
            />
        </Pressable>
    )
}

const FileGridCard = memo(FileGridCardImpl)
function FileGridCardImpl({ item, ctx }: { item: DriveItemView; ctx: CellContext }) {
    const { actions } = ctx
    const isSelectedRow = ctx.isSelected(item.id)

    // Desktop: select on press-down (onPressIn) so the highlight is instant,
    // and open on double-click via onPress. Selection no longer rides onPress,
    // which fired on release and lagged behind the press.
    const handleSelectIn = useCallback(
        (event: GestureResponderEvent) => ctx.handleSelect(item.id, event),
        [item.id, ctx]
    )
    // On a plain single-click, collapse a preserved multi-selection to this card
    // (handleSelect leaves it intact on press-down so a grab can drag the whole
    // selection). Runs only on a real click — a drag consumes the pointer.
    const handleClickSelect = useCallback(
        (event: GestureResponderEvent) => ctx.handleSelectClick(item.id, event),
        [item.id, ctx]
    )
    const handleOpen = useCallback(() => ctx.openFile(item), [item, ctx])
    const handleShowDetails = useShowDetailsAfterClick(ctx)
    const handleDesktopOpen = useDoubleClick(handleClickSelect, handleOpen, handleShowDetails)
    const handleMobilePress = useCallback(() => {
        if (consumeContextMenuPressSuppression(Date.now())) return
        ctx.openFile(item)
    }, [item, ctx])

    return (
        <Pressable
            onPressIn={ctx.isMobile ? undefined : handleSelectIn}
            onPress={ctx.isMobile ? handleMobilePress : handleDesktopOpen}
            // No "button" role — the card hosts its own buttons (⋯ menu);
            // nesting <button> in <button> is invalid on web.
            accessibilityLabel={`${item.name} ${formatDate(item.updated)}`}
            // Tags the card for the drag-to-select hit-test (its rect == the
            // visible tile). Renders as data-drive-item-id on web; no-op native.
            dataSet={{ driveItemId: item.id }}
            style={{ height: GRID_CARD_HEIGHT }}
            className={gridCardClassName(item, isSelectedRow)}
        >
            <GridCardFace
                item={item}
                mutedColor={ctx.mutedColor}
                headerActions={<GridCardActions item={item} actions={actions} />}
            />
        </Pressable>
    )
}

function gridCardClassName(item: DriveItemView, isSelected: boolean) {
    const clip = item.isFolder ? '' : 'overflow-hidden '
    const border = isSelected ? 'border-2 border-active-indicator' : 'border-border'
    return `rounded-lg ${clip}border ${border}`
}

// What a grid card shows, shared by the live card and its drag preview so the
// two cannot drift apart. A folder's header is a little roomier than a file's,
// and a file shows its thumbnail where a folder shows a large icon.
function GridCardFace({
    item,
    mutedColor,
    headerActions,
}: {
    item: DriveItemView
    mutedColor: string
    /** The star + ⋯ buttons, or their look without the buttons in a preview. */
    headerActions: ReactNode
}) {
    const { icon: FileIcon, color: iconColor } = getFileIcon(item.category, mutedColor)
    const header = item.isFolder
        ? { className: 'gap-2.5 px-3 py-2.5', iconSize: 20 }
        : { className: 'gap-2 px-2.5 py-2', iconSize: 18 }
    return (
        <>
            <View className={`flex-row items-center ${header.className} border-b border-border`}>
                <FileIcon size={header.iconSize} color={iconColor} />
                <Text numberOfLines={1} className="flex-1 text-xs font-medium text-foreground">
                    {item.name}
                </Text>
                {headerActions}
            </View>
            <View className="flex-1 items-center justify-center bg-muted-foreground/5">
                <GridCardBody item={item} icon={FileIcon} iconColor={iconColor} />
            </View>
        </>
    )
}

function GridCardBody({
    item,
    icon: FileIcon,
    iconColor,
}: {
    item: DriveItemView
    icon: FileIconComponent
    iconColor: string
}) {
    if (!item.isFolder) return <Thumbnail item={item} size={120} />
    return <FileIcon size={56} color={iconColor} />
}

type FileIconComponent = ReturnType<typeof getFileIcon>['icon']

function GridCardActions({ item, actions }: { item: DriveItemView; actions: DriveActions }) {
    return (
        <>
            <Pressable
                style={{ padding: 2 }}
                onPress={e => {
                    e.stopPropagation()
                    actions.toggleStar(item.id)
                }}
            >
                <StarIcon isStarred={item.starred} size={14} />
            </Pressable>
            <DriveItemMenuButton item={item} size={14} />
        </>
    )
}

function GridCardStaticActions({ item }: { item: DriveItemView }) {
    return (
        <>
            <View style={{ padding: 2 }}>
                <StarIcon isStarred={item.starred} size={14} />
            </View>
            <DriveItemMenuIcon size={14} />
        </>
    )
}

// The card's copy that follows the pointer: the same face, inert, on an opaque
// background (the live card is transparent over the page), at the width the
// dragged card measured.
function GridCardDragPreview({
    item,
    mutedColor,
    width,
}: {
    item: DriveItemView
    mutedColor: string
    width: number | undefined
}) {
    const isSelected = useDriveUIStore(s => s.selectedIds.has(item.id))
    return (
        <View
            className={`${gridCardClassName(item, isSelected)} bg-background shadow-md`}
            style={{ width, height: GRID_CARD_HEIGHT }}
        >
            <GridCardFace
                item={item}
                mutedColor={mutedColor}
                headerActions={<GridCardStaticActions item={item} />}
            />
        </View>
    )
}
